"use client";

import {usePathname} from "next/navigation";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {useOrgSites} from "@/contexts/sites/OrgSitesContext";
import {GailProductRoles, Permissions, RoleData, UserRoles} from "@/enums/user";
import {useEodReportAlert} from "@/hooks/useEodReportAlert";
import {useOrg} from "@/hooks/useOrg";
import {usePermissions} from "@/hooks/usePermissions";
import type {GlobalNavSectionType} from "@/types/common/library";
import {canAccessNavItem} from "@/utils/authz/navAccess";
import {BASE_SUPPORT_URL} from "@/utils/common/baseUrls";
import {
   canSeeReportingNav,
   getReportingNavHref,
} from "@/utils/reporting/access";
import {sitesNavSection, withSitesNavSection} from "@/utils/sites/nav";
import BaseNavContainer from "./BaseNavContainer";

export default function NoProductAccessNavContainer({
   children,
   onUserManagementClick,
}: {
   children: React.ReactNode;
   onUserManagementClick?: () => void;
}) {
   const pathname = usePathname();
   const {role, productRolesState} = useAuthContext();
   const {productAccess} = useOrg();
   const {canAccess, canAccessAny, permissions} = usePermissions();
   const {hasAlert: hasEodReportAlert} = useEodReportAlert();

   const {t} = useTranslation("routes");
   const {t: ct} = useTranslation("common");
   const {sites} = useOrgSites();
   const hasSites = sites.length > 0;
   const isReportUser =
      productRolesState?.roles?.includes(GailProductRoles.ReportUser) ?? false;
   const gailAgentEnabled = productAccess.gailAgentEnabled ?? false;
   const showReporting = canSeeReportingNav(
      gailAgentEnabled,
      isReportUser,
      canAccess(RoleData.Reporting, Permissions.Read)
   );

   const sections = useMemo<GlobalNavSectionType[]>(() => {
      let bottomLinkType: "settings" | "upgrade" | "none" = "none";

      if (productAccess.orgHasActiveProducts && role === UserRoles.Admin) {
         bottomLinkType = "settings";
      } else if (!productAccess.orgHasActiveProducts) {
         bottomLinkType = "upgrade";
      }

      let navSections: GlobalNavSectionType[] = [];

      if (showReporting) {
         navSections.push({
            name: t("dataAndAnalytics"),
            position: "top",
            items: [
               {
                  icon: "chart-pie-simple",
                  label: ct("reporting"),
                  href: getReportingNavHref(gailAgentEnabled, isReportUser),
                  isActive: pathname.startsWith("/reporting"),
                  dataType: RoleData.Reporting,
                  hasAlert: hasEodReportAlert,
               },
            ],
         });
      }

      const resources: GlobalNavSectionType = {
         position: "bottom",
         items: [
            ...(bottomLinkType === "settings"
               ? [
                    {
                       icon: "gear" as const,
                       label: t("settings"),
                       href: "/settings/general",
                       isActive: pathname.startsWith("/settings"),
                       dataType: RoleData.Settings,
                    },
                 ]
               : []),
            ...(bottomLinkType === "upgrade"
               ? [
                    {
                       icon: "rocket" as const,
                       label: t("upgrade"),
                       href: "/get-gail",
                       isActive: pathname.startsWith("/get-gail"),
                       dataType: RoleData.Settings,
                    },
                 ]
               : []),
            {
               icon: "circle-question" as const,
               label: t("support"),
               href: BASE_SUPPORT_URL,
               target: "_blank",
               dataType: RoleData.Support,
            },
         ],
      };

      navSections.push(resources);
      navSections = withSitesNavSection(
         navSections,
         sitesNavSection(hasSites, pathname.split("/").filter(Boolean), t("sites"))
      );

      //Go through sections and remove any sections
      // that the user does not have access to based on the hook
      navSections.forEach((section) => {
         section.items = section.items.filter((item) => {
            if (item.dataType === RoleData.Reporting) {
               return showReporting;
            }
            return canAccessNavItem(item, canAccessAny);
         });
      });

      //Remove any sections that have no items
      navSections = navSections.filter((section) => section.items.length > 0);

      return navSections;
   }, [
      pathname,
      t,
      ct,
      role,
      productAccess.userHasAnyProductAccess,
      productAccess.orgHasActiveProducts,
      permissions,
      hasEodReportAlert,
      gailAgentEnabled,
      isReportUser,
      showReporting,
      hasSites,
   ]);

   return (
      <BaseNavContainer menuSections={sections} onUserManagementClick={onUserManagementClick}>
         {children}
      </BaseNavContainer>
   );
}
